import React, { useEffect, useState } from 'react';
import { Plus, CheckCircle, X, ChevronDown, ChevronRight } from 'lucide-react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';

const fmt = (n) => `€${(n||0).toFixed(2)}`;
const STATUS_COLOR = { BOZZA:'#666', INVIATO:'#3B82F6', RICEVUTO_PARZIALE:'#F59E0B', COMPLETO:'#22C55E', ANNULLATO:'#EF4444' };

export default function PurchaseOrders() {
  const { user } = useAuth();
  const puoCreare     = puoFare(user, 'fornitori', 'create');
  const puoModificare = puoFare(user, 'fornitori', 'edit');
  const [orders, setOrders]       = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts]   = useState([]);
  const [showNew, setShowNew]     = useState(false);
  const [receiveModal, setReceiveModal] = useState(null);
  const [ddtForm, setDdtForm]     = useState({ numero_ddt:'', data_ddt: new Date().toISOString().split('T')[0] });
  const [expanded, setExpanded]   = useState({});

  // Form nuovo ordine acquisto
  const [supplierId, setSupplierId] = useState('');
  const [noteOrdine, setNoteOrdine] = useState('');
  const [items, setItems]         = useState([{ product_id:'', variant_id:'', taglia:'', quantita:1 }]);

  const load = () => Promise.all([
    api.get('/suppliers/purchase-orders'),
    api.get('/suppliers'),
    api.get('/products'),
  ]).then(([o, s, p]) => {
    setOrders(o.data);
    setSuppliers(s.data);
    setProducts(p.data);
  });

  useEffect(() => { load(); }, []);

  // Prodotti filtrati per fornitore selezionato
  const prodottiFornitore = supplierId
    ? products.filter(p => p.supplier_id === parseInt(supplierId))
    : [];

  const getProduct = (pid) => products.find(p => p.id === parseInt(pid));

  const getTaglie = (pid) => {
    const prod = getProduct(pid);
    return prod?.variants?.filter(v => v.attiva) || [];
  };

  const addItem = () => setItems(prev => [...prev, { product_id:'', variant_id:'', taglia:'', quantita:1 }]);

  const updateItem = (idx, key, val) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const updated = { ...it, [key]: val };
      if (key === 'product_id') { updated.variant_id = ''; updated.taglia = ''; }
      if (key === 'taglia') {
        // Trova la variant_id dalla taglia selezionata
        const prod = getProduct(it.product_id);
        const v = prod?.variants?.find(v => v.taglia === val);
        updated.variant_id = v ? String(v.id) : '';
      }
      return updated;
    }));
  };

  const resetForm = () => {
    setSupplierId('');
    setNoteOrdine('');
    setItems([{ product_id:'', variant_id:'', taglia:'', quantita:1 }]);
    setShowNew(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!supplierId) { alert('Seleziona un fornitore'); return; }
    if (items.some(i => !i.product_id || !i.taglia)) { alert('Completa tutti gli articoli con prodotto e taglia'); return; }

    try {
      await api.post(`/suppliers/${supplierId}/purchase-orders`, {
        note: noteOrdine,
        items: items.map(i => {
          const prod = getProduct(i.product_id);
          return {
            product_id: parseInt(i.product_id),
            taglia: i.taglia,
            quantita: parseInt(i.quantita),
            prezzo_imponibile: prod?.prezzo_acquisto_imponibile || 0,
          };
        }),
      });
      resetForm();
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Errore creazione ordine');
    }
  };

  const handleReceive = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/suppliers/purchase-orders/${receiveModal.id}/receive`, ddtForm);
      setReceiveModal(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Errore');
    }
  };

  const toggleExpand = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const cancelOrder = async (id) => {
    if (!window.confirm('Annullare questo ordine di acquisto?')) return;
    try {
      await api.patch(`/suppliers/purchase-orders/${id}/cancel`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Errore');
    }
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'24px' }}>
        <div>
          <h1 style={s.title}>ORDINI DI ACQUISTO</h1>
          <p style={s.sub}>{orders.length} ordini</p>
        </div>
        {puoCreare && <button style={s.btnPri} onClick={() => setShowNew(true)}><Plus size={15}/> NUOVO ORDINE</button>}
      </div>

      {/* ── FORM NUOVO ORDINE ── */}
      {showNew && (
        <div style={s.formCard}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
            <div style={s.formTitle}>NUOVO ORDINE DI ACQUISTO</div>
            <button style={{ background:'transparent', border:'none', color:'var(--text-dim)', cursor:'pointer' }} onClick={resetForm}><X size={16}/></button>
          </div>

          <form onSubmit={handleCreate} style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
            {/* Fornitore */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              <div style={s.field}>
                <label style={s.label}>FORNITORE *</label>
                <select style={s.input} value={supplierId} onChange={e => { setSupplierId(e.target.value); setItems([{ product_id:'', variant_id:'', taglia:'', quantita:1 }]); }} required>
                  <option value="">— seleziona fornitore —</option>
                  {suppliers.map(su => <option key={su.id} value={su.id}>{su.ragione_sociale}</option>)}
                </select>
              </div>
              <div style={s.field}>
                <label style={s.label}>NOTE</label>
                <input style={s.input} value={noteOrdine} onChange={e => setNoteOrdine(e.target.value)} placeholder="Note opzionali..." />
              </div>
            </div>

            {/* Avviso nessun prodotto */}
            {supplierId && prodottiFornitore.length === 0 && (
              <div style={{ background:'#F59E0B10', border:'1px solid #F59E0B30', borderRadius:'8px', padding:'12px', fontSize:'var(--font-size-xs)', color:'#F59E0B' }}>
                ⚠️ Nessun prodotto associato a questo fornitore. Vai nel Catalogo e assegna i prodotti al fornitore.
              </div>
            )}

            {/* Articoli */}
            {supplierId && prodottiFornitore.length > 0 && (
              <div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
                  <label style={s.label}>ARTICOLI</label>
                  <button type="button" style={s.btnSec} onClick={addItem}><Plus size={12}/> RIGA</button>
                </div>

                {/* Header colonne */}
                <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 70px 100px', gap:'8px', marginBottom:'6px' }}>
                  {['PRODOTTO','TAGLIA','QTÀ','PREZZO ACQ.'].map(h => (
                    <div key={h} style={{ fontSize: 'var(--font-size-xs)', letterSpacing:'1.5px', fontWeight:'700', color:'var(--text-dim)' }}>{h}</div>
                  ))}
                </div>

                {items.map((item, idx) => {
                  const prod = getProduct(item.product_id);
                  const taglie = getTaglie(item.product_id);
                  return (
                    <div key={idx} style={{ display:'grid', gridTemplateColumns:'2fr 1fr 70px 100px 30px', gap:'8px', marginBottom:'8px', alignItems:'center' }}>
                      <select style={s.input} value={item.product_id} onChange={e => updateItem(idx, 'product_id', e.target.value)}>
                        <option value="">— prodotto —</option>
                        {prodottiFornitore.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                      </select>

                      <select style={s.input} value={item.taglia} onChange={e => updateItem(idx, 'taglia', e.target.value)} disabled={!item.product_id}>
                        <option value="">— taglia —</option>
                        {taglie.map(v => <option key={v.id} value={v.taglia}>{v.taglia}</option>)}
                      </select>

                      <input style={s.input} type="number" min="1" value={item.quantita}
                        onChange={e => updateItem(idx, 'quantita', e.target.value)} />

                      <div style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight:'800', color:'var(--text-muted)', padding:'8px 0' }}>
                        {prod ? fmt(prod.prezzo_acquisto_imponibile) : '—'}
                      </div>

                      <button type="button" style={{ background:'#EF444415', border:'1px solid #EF444430', borderRadius:'6px', padding:'6px', color:'#EF4444', cursor:'pointer' }}
                        onClick={() => setItems(prev => prev.filter((_, i) => i !== idx))}>
                        <X size={12}/>
                      </button>
                    </div>
                  );
                })}

                {/* Totale */}
                {items.some(i => i.product_id) && (
                  <div style={{ display:'flex', justifyContent:'flex-end', paddingTop:'12px', borderTop:'1px solid var(--border)' }}>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontSize: 'var(--font-size-xs)', color:'var(--text-dim)', letterSpacing:'1px', marginBottom:'2px' }}>TOTALE IMPONIBILE</div>
                      <div style={{ fontFamily:"'Barlow Condensed'", fontSize:'var(--font-size-xl)', fontWeight:'800', color:'#F4600C' }}>
                        {fmt(items.reduce((s, i) => {
                          const p = getProduct(i.product_id);
                          return s + (p ? p.prezzo_acquisto_imponibile * (parseInt(i.quantita) || 0) : 0);
                        }, 0))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end' }}>
              <button type="button" style={s.btnSec} onClick={resetForm}>ANNULLA</button>
              <button type="submit" style={s.btnPri} disabled={!supplierId || prodottiFornitore.length === 0}>CREA ORDINE</button>
            </div>
          </form>
        </div>
      )}

      {/* ── MODAL RICEZIONE ── */}
      {receiveModal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.formTitle}>REGISTRA RICEZIONE MERCE</div>
            <div style={{ color:'var(--text-dim)', fontSize:'var(--font-size-xs)', marginBottom:'20px', marginTop:'4px' }}>{receiveModal.numero_acquisto}</div>
            <form onSubmit={handleReceive} style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
              <div style={s.field}>
                <label style={s.label}>NUMERO DDT *</label>
                <input style={s.input} value={ddtForm.numero_ddt} onChange={e => setDdtForm(f => ({...f, numero_ddt:e.target.value}))} required autoFocus />
              </div>
              <div style={s.field}>
                <label style={s.label}>DATA DDT *</label>
                <input style={s.input} type="date" value={ddtForm.data_ddt} onChange={e => setDdtForm(f => ({...f, data_ddt:e.target.value}))} required />
              </div>
              <div style={{ background:'#F59E0B10', border:'1px solid #F59E0B30', borderRadius:'8px', padding:'12px', fontSize:'var(--font-size-xs)', color:'#F59E0B' }}>
                ⚠️ Confermando, il magazzino verrà caricato automaticamente e l'uscita registrata in cassa.
              </div>
              <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end' }}>
                <button type="button" style={s.btnSec} onClick={() => setReceiveModal(null)}>ANNULLA</button>
                <button type="submit" style={{ ...s.btnPri, background:'#22C55E' }}>✓ CONFERMA RICEZIONE</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── LISTA ORDINI ── */}
      <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
        {orders.map(o => {
          const colore = STATUS_COLOR[o.stato] || '#666';
          const isOpen = expanded[o.id];
          return (
            <div key={o.id} style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'10px', overflow:'hidden' }}>
              {/* Header */}
              <div style={{ display:'flex', alignItems:'center', gap:'14px', padding:'14px 20px', cursor:'pointer' }}
                onClick={() => toggleExpand(o.id)}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'4px' }}>
                    <span style={{ fontFamily:"'Barlow Condensed'", fontWeight:'800', fontSize:'var(--font-size-base)' }}>{o.numero_acquisto}</span>
                    <span style={{ background:`${colore}20`, color:colore, border:`1px solid ${colore}40`, borderRadius:'4px', padding:'2px 8px', fontSize: 'var(--font-size-xs)', fontWeight:'700' }}>{o.stato}</span>
                  </div>
                  <div style={{ color:'var(--text-muted)', fontSize:'var(--font-size-sm)' }}>{o.supplier?.ragione_sociale}</div>
                  <div style={{ color:'var(--text-dim)', fontSize:'var(--font-size-xs)', marginTop:'2px' }}>
                    {o.items?.length} articoli · {new Date(o.created_at).toLocaleDateString('it-IT')}
                    {o.numero_ddt && ` · DDT: ${o.numero_ddt}`}
                  </div>
                </div>
                <div style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-xl)', fontWeight:'800' }}>{fmt(o.totale)}</div>
                {puoModificare && o.stato !== 'COMPLETO' && o.stato !== 'ANNULLATO' && (
                  <>
                    <button style={{ display:'flex', alignItems:'center', gap:'6px', background:'#22C55E20', border:'1px solid #22C55E40', borderRadius:'6px', padding:'8px 14px', color:'#22C55E', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' }}
                      onClick={(e) => { e.stopPropagation(); setReceiveModal(o); setDdtForm({ numero_ddt:'', data_ddt: new Date().toISOString().split('T')[0] }); }}>
                      <CheckCircle size={13}/> RICEVI MERCE
                    </button>
                    <button style={{ display:'flex', alignItems:'center', gap:'6px', background:'#EF444420', border:'1px solid #EF444440', borderRadius:'6px', padding:'8px 14px', color:'#EF4444', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' }}
                      onClick={(e) => { e.stopPropagation(); cancelOrder(o.id); }}>
                      <X size={13}/> ANNULLA
                    </button>
                  </>
                )}
                {isOpen ? <ChevronDown size={14} color="#555"/> : <ChevronRight size={14} color="#555"/>}
              </div>

              {/* Dettaglio articoli */}
              {isOpen && (
                <div style={{ borderTop:'1px solid var(--border)', padding:'0' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'2fr 80px 60px 100px', gap:'8px', padding:'8px 20px', background:'var(--bg)', fontSize: 'var(--font-size-xs)', letterSpacing:'1.5px', fontWeight:'700', color:'var(--text-dim)' }}>
                    <span>PRODOTTO</span><span style={{ textAlign:'center' }}>TAGLIA</span><span style={{ textAlign:'center' }}>QTÀ</span><span style={{ textAlign:'right' }}>PREZZO IMP.</span>
                  </div>
                  {o.items?.map(item => (
                    <div key={item.id} style={{ display:'grid', gridTemplateColumns:'2fr 80px 60px 100px', gap:'8px', padding:'10px 20px', borderTop:'1px solid #111', alignItems:'center' }}>
                      <span style={{ fontSize:'var(--font-size-sm)', fontWeight:'600' }}>{item.product?.nome}</span>
                      <span style={{ textAlign:'center' }}>
                        <span style={{ background:'var(--border)', border:'1px solid var(--border2)', borderRadius:'4px', padding:'2px 10px', fontSize:'var(--font-size-xs)', fontWeight:'700', color:'var(--text-muted)' }}>{item.taglia || '—'}</span>
                      </span>
                      <span style={{ textAlign:'center', fontSize:'var(--font-size-sm)', fontWeight:'700' }}>{item.quantita}</span>
                      <span style={{ textAlign:'right', fontFamily:"'Barlow Condensed'", fontSize:'var(--font-size-base)', fontWeight:'800' }}>{fmt(item.prezzo_imponibile)}</span>
                    </div>
                  ))}
                  {o.note && (
                    <div style={{ padding:'10px 20px', color:'var(--text-dim)', fontSize:'var(--font-size-xs)', borderTop:'1px solid #111' }}>📝 {o.note}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {orders.length === 0 && (
          <div style={{ textAlign:'center', color:'var(--text-dim)', padding:'50px 0' }}>Nessun ordine di acquisto</div>
        )}
      </div>
    </div>
  );
}

const s = {
  title: { fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight:'800', letterSpacing:'2px' },
  sub: { color:'var(--text-dim)', fontSize:'var(--font-size-sm)', marginTop:'4px' },
  btnPri: { display:'flex', alignItems:'center', gap:'8px', background:'#F4600C', color:'#fff', border:'none', borderRadius:'8px', padding:'11px 18px', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  btnSec: { background:'var(--border)', color:'var(--text)', border:'1px solid var(--border2)', borderRadius:'8px', padding:'9px 14px', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px' },
  formCard: { background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'12px', padding:'24px', marginBottom:'20px' },
  formTitle: { fontFamily:"'Barlow Condensed'", fontSize:'var(--font-size-base)', fontWeight:'700', letterSpacing:'2px' },
  field: { display:'flex', flexDirection:'column', gap:'6px' },
  label: { fontSize: 'var(--font-size-xs)', letterSpacing:'2px', fontWeight:'700', color:'var(--text-dim)' },
  input: { background:'var(--bg-input)', border:'1px solid var(--border2)', borderRadius:'8px', padding:'9px 12px', color:'var(--text)', fontSize:'var(--font-size-sm)', outline:'none', width:'100%', boxSizing:'border-box' },
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 },
  modal: { background:'var(--bg-card)', border:'1px solid #222', borderRadius:'12px', padding:'28px', minWidth:'380px' },
};
